import { Alert, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { TAX_ADVISORY_PERMISSION_SCOPES, useAuth } from "@/modules/auth";
import { useUserContext } from "../../../../contexts/UserContext";
import { useQuestionnairesQuery } from "../../../../../../../api/graphql/queries/questionnaire/questionnaires.generated";
import { useTaxCaseQuery } from "../../../../../../../api/graphql/queries/taxCase/taxCase.generated";
import { Questionnaire } from "../../../../../../../api/graphql/schema.generated";
import { formatDate } from "../../../../utils";
import EmptyWrapper from "../../../../../../common/EmptyWrapper";
import useEmailParam from "../../../../../../hooks/useEmailParam";
import useTaxYearParam from "../../../../hooks/useTaxYearParam";
import { hasCompanyQuestionnaireRestriction } from "../utils";
import useUnavailableQuestionnaireTypes from "../useUnavailableQuestionnaireTypes";
import { PRIVATE_TAX_RETURN_QUESTIONNAIRE_TYPES } from "./constants";
import BookkeepingSection from "./BookkeepingSection";
import {
  getBusinessCompleteness,
  getPrivateCompleteness,
} from "./completeness";
import {
  getQuestionnaireEntries,
  QuestionnaireEntry,
} from "./questionnaireEntries";
import TaxReturnSection from "./TaxReturnSection";
import { TitleRow, TitleSide } from "./styledComponents";

const { Title, Text } = Typography;

const FIRST_SUPPORTED_TAX_YEAR = 2021;

const TaxPreparationOverview = () => {
  const { t } = useTranslation("TaxCaseOverview", {
    keyPrefix: "companyHints",
  });
  const { t: tOverview } = useTranslation("TaxCaseOverview", {
    keyPrefix: "completenessOverview",
  });
  const user = useUserContext();
  const [taxYear] = useTaxYearParam();
  const [email] = useEmailParam();

  const { hasOperatorPermissionScope } = useAuth();
  const hasTaxAdvisoryPermissionScope = hasOperatorPermissionScope(
    TAX_ADVISORY_PERMISSION_SCOPES
  );

  const isSupportedTaxYear = taxYear >= FIRST_SUPPORTED_TAX_YEAR;
  const { data: questionnairesData, loading: isLoadingQuestionnaires } =
    useQuestionnairesQuery({
      skip: !email || !isSupportedTaxYear,
      variables: {
        email: email!,
        year: taxYear,
      },
    });
  const questionnaires = questionnairesData?.questionnaires || [];

  const { data: taxCaseData, loading: isLoadingTaxCase } = useTaxCaseQuery({
    skip: !email || !isSupportedTaxYear || !hasTaxAdvisoryPermissionScope,
    variables: {
      email: email!,
      year: taxYear,
    },
  });
  const { unavailableTypes, loading: isLoadingUnavailableTypes } =
    useUnavailableQuestionnaireTypes({
      email,
      taxYear,
      skip: !isSupportedTaxYear,
    });

  const isIncomeTaxSkipped = !!taxCaseData?.taxCase?.incomeTaxSkippedAt;

  const questionnaireEntries = getQuestionnaireEntries({
    taxYear,
    questionnaires: questionnaires as Questionnaire[],
    unavailableTypes,
  });
  const taxCase = taxCaseData?.taxCase;
  const deadline = formatDate(taxCase?.deadline);
  const submittedAt = formatDate(taxCase?.userFinalizedAt);

  const isPrivateEntry = ({ info }: QuestionnaireEntry) =>
    PRIVATE_TAX_RETURN_QUESTIONNAIRE_TYPES.includes(info.type);
  const businessEntries = questionnaireEntries.filter(
    (entry) => !isPrivateEntry(entry)
  );
  const privateEntries = questionnaireEntries.filter(isPrivateEntry);

  const isLoading = isLoadingQuestionnaires || isLoadingTaxCase;

  if (!email) {
    return <EmptyWrapper description="Suche nach einem Mandanten" />;
  }

  if (!isSupportedTaxYear) {
    return (
      <EmptyWrapper
        description={`Vorbereitung der Steuererklärung wird erst ab ${FIRST_SUPPORTED_TAX_YEAR} unterstützt`}
      />
    );
  }

  return (
    <>
      <TitleRow className="mb-3">
        <Title level={3} style={{ marginBottom: 0 }}>
          {tOverview("title", { year: taxYear })}
        </Title>
        {taxCase && (
          <TitleSide>
            {deadline && (
              <Text>{tOverview("deadline", { date: deadline })}</Text>
            )}
            <Text>
              {tOverview("allDataSubmitted", {
                date: submittedAt ?? tOverview("open"),
              })}
            </Text>
          </TitleSide>
        )}
      </TitleRow>

      {!isLoadingUnavailableTypes &&
        user?.companyType &&
        hasCompanyQuestionnaireRestriction(unavailableTypes) && (
          <Alert
            className="mb-3"
            type="info"
            showIcon
            message={t("questionnaires", { legalForm: user.companyType })}
          />
        )}

      <BookkeepingSection taxYear={taxYear} />

      <TaxReturnSection
        title={tOverview("businessTaxReturn")}
        completeness={taxCase && getBusinessCompleteness(taxCase)}
        entries={businessEntries}
        taxYear={taxYear}
        isLoading={isLoading}
        isLoadingEntries={isLoadingUnavailableTypes}
        isNotRelevant={false}
      />

      <TaxReturnSection
        title={tOverview("privateTaxReturn")}
        // Depends on the unavailable types: wait for them, else a company
        // briefly shows "offen" before flipping to "nicht relevant".
        completeness={
          taxCase && !isLoadingUnavailableTypes
            ? getPrivateCompleteness(taxCase, unavailableTypes)
            : undefined
        }
        entries={privateEntries}
        taxYear={taxYear}
        isLoading={isLoading}
        isLoadingEntries={isLoadingUnavailableTypes}
        isNotRelevant={isIncomeTaxSkipped}
      />
    </>
  );
};

export default TaxPreparationOverview;
